import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AlertCircle, Check, Play } from "lucide-react";
import { Alert, AlertDescription } from "@pgs-core/ui/components/alert";
import { Badge } from "@pgs-core/ui/components/badge";
import { Button } from "@pgs-core/ui/components/button";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@pgs-core/ui/components/table";
import { formatTimestamp } from "@pgs-core/portal-lib/format";
import { useApiQuery } from "@pgs-core/portal-lib/hooks";
import {
  fetchSupportRequests,
  hoursOpen,
  SLA_TURNAROUND_HOURS,
  SLA_WARNING_HOURS,
  updateSupportRequest,
  type AdminSupportRequest,
} from "../lib/support";
import type { AdminOutletContext } from "../lib/outlet-context";

function statusBadge(request: AdminSupportRequest) {
  if (request.status === "done") return <Badge variant="success">Done</Badge>;
  if (request.status === "in_progress") return <Badge variant="warning">In progress</Badge>;
  return <Badge variant="secondary">Open</Badge>;
}

/** Time-open cell: red once the 24-hour SLA window is three-quarters gone. */
function ElapsedCell({ request }: { request: AdminSupportRequest }) {
  const hours = hoursOpen(request);
  if (hours === null) return <span className="text-muted-foreground">—</span>;
  if (hours >= SLA_TURNAROUND_HOURS) {
    return <span className="font-semibold text-destructive">{hours}h — SLA breached</span>;
  }
  if (hours >= SLA_WARNING_HOURS) {
    return <span className="font-semibold text-destructive">{hours}h of {SLA_TURNAROUND_HOURS}h</span>;
  }
  return <span>{hours}h of {SLA_TURNAROUND_HOURS}h</span>;
}

export function SupportPage() {
  const { api } = useOutletContext<AdminOutletContext>();
  const [view, setView] = useState<"active" | "done">("active");
  const [actionError, setActionError] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const query = useApiQuery(
    () => (api ? fetchSupportRequests(api, view) : null),
    [api, view]
  );
  const requests = query.data?.requests ?? [];
  const clients = query.data?.clients ?? [];

  const act = async (id: string, status: "in_progress" | "done") => {
    if (!api) return;
    setBusyId(id);
    setActionError("");
    try {
      await updateSupportRequest(api, id, status);
      query.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Support requests</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Update requests from SLA clients — oldest first, 24-hour turnaround. Marking one
          complete emails the client.
        </p>
      </div>

      <div className="mb-4 flex gap-2">
        <Button
          variant={view === "active" ? "default" : "outline"}
          size="sm"
          onClick={() => setView("active")}
        >
          Open
        </Button>
        <Button
          variant={view === "done" ? "default" : "outline"}
          size="sm"
          onClick={() => setView("done")}
        >
          Completed
        </Button>
      </div>

      {(query.error || actionError) && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{query.error || actionError}</AlertDescription>
        </Alert>
      )}

      {query.data !== null && requests.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
          <p className="text-sm text-muted-foreground">
            {view === "active" ? "No open requests — all caught up." : "No completed requests yet."}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Request</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>{view === "active" ? "Time open" : "Completed"}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell className="whitespace-nowrap font-medium">
                    {request.business_name ?? request.tenant_id}
                  </TableCell>
                  <TableCell className="max-w-md">
                    <p className="whitespace-pre-wrap text-sm">{request.description}</p>
                    {request.image_key && (
                      <p className="mt-1 text-xs text-muted-foreground" title={request.image_key}>
                        Image attached (in the client's media library)
                      </p>
                    )}
                  </TableCell>
                  <TableCell>{statusBadge(request)}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatTimestamp(request.created_at)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {view === "active"
                      ? <ElapsedCell request={request} />
                      : <span className="text-muted-foreground">{formatTimestamp(request.completed_at)}</span>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {request.status === "open" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mr-2"
                        disabled={busyId === request.id}
                        onClick={() => act(request.id, "in_progress")}
                      >
                        <Play className="h-3.5 w-3.5" />
                        Start
                      </Button>
                    )}
                    {request.status !== "done" && (
                      <Button
                        size="sm"
                        disabled={busyId === request.id}
                        onClick={() => act(request.id, "done")}
                      >
                        <Check className="h-3.5 w-3.5" />
                        Mark complete
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {clients.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-3 text-sm font-semibold">Monthly quota by client</h2>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Updates used this month</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.tenant_id}>
                    <TableCell className="font-medium">
                      {client.business_name ?? client.tenant_id}
                    </TableCell>
                    <TableCell>
                      <span className={client.used >= client.limit ? "font-semibold text-destructive" : undefined}>
                        {client.used} of {client.limit}
                      </span>
                      {client.used >= client.limit && (
                        <span className="ml-2 text-xs text-muted-foreground">quota reached</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
